<template>
  <div>
    <PageHeader
      title="Data"
      subtitle="Ingestion, feeds, and imports"
    >
      <template #bottom>
        <Page_Tabs
          class="px-6 pt-4"
          :initial-tab="selected_tab"
          :tabs="etl_tabs"
          no-border
          @select="switch_tab"
        />
      </template>
    </PageHeader>

    <!-- Panes mount on first visit and stay alive (v-show) so tab switches
         never destroy in-progress state (uploads, classification jobs, edits). -->
    <div class="p-6">
      <div v-if="visited.has('Feeds')" v-show="selected_tab === 'Feeds'">
        <Overview_ETL_Feed_Registry @open-ingestion="openIngestion" />
      </div>
      <div v-if="visited.has('Catalog')" v-show="selected_tab === 'Catalog'">
        <Overview_ETL_Data_Catalog />
      </div>
      <div v-if="visited.has('Ingestion')" v-show="selected_tab === 'Ingestion'">
        <Overview_ETL_Ingestion ref="ingestion" />
      </div>
      <div v-if="visited.has('MDR')" v-show="selected_tab === 'MDR'">
        <MDR />
      </div>
      <div v-if="visited.has('Reconciliation')" v-show="selected_tab === 'Reconciliation'">
        <Overview_ETL_Reconciliation />
      </div>
      <div v-if="visited.has('Data Attributes')" v-show="selected_tab === 'Data Attributes'">
        <Overview_ETL_Data_Attributes />
      </div>
      <div v-if="visited.has('Workflow Builder')" v-show="selected_tab === 'Workflow Builder'">
        <Overview_ETL_Workflow_Builder />
      </div>
      <div v-if="visited.has('Uploads')" v-show="selected_tab === 'Uploads'">
        <Customer_File_Upload />
      </div>
    </div>
  </div>
</template>

<script>
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Overview_ETL_Feed_Registry from '@/components/ETL_Utility/Overview_ETL_Feed_Registry.vue';
import Overview_ETL_Data_Catalog from '@/components/ETL_Utility/Overview_ETL_Data_Catalog.vue';
import Overview_ETL_Ingestion from '@/components/ETL_Utility/Ingestion/Overview_ETL_Ingestion.vue';
import MDR from '@/components/ETL_Utility/mdr/MDR.vue';
import Overview_ETL_Reconciliation from '@/components/ETL_Utility/Reconciliation/Overview_ETL_Reconciliation.vue';
import Overview_ETL_Data_Attributes from '@/components/ETL_Utility/Overview_ETL_Data_Attributes.vue';
import Overview_ETL_Workflow_Builder from '@/components/ETL_Utility/Overview_ETL_Workflow_Builder.vue';
import Customer_File_Upload from '@/components/DataUpload/Customer_File_Upload.vue';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';

export default {
  name: 'OverviewListETLUtility',
  components: {
    Page_Tabs,
    PageHeader,
    Overview_ETL_Feed_Registry,
    Overview_ETL_Ingestion,
    MDR,
    Overview_ETL_Reconciliation,
    Overview_ETL_Data_Attributes,
    Overview_ETL_Workflow_Builder,
    Customer_File_Upload,
    Overview_ETL_Data_Catalog,
  },
  data() {
    return {
      selected_tab: null,
      visited: new Set(),
    };
  },
  computed: {
    etl_tabs() {
      const tabs = [];
      if (canAccessFeature(FEATURES.MODULE_DATA_FEED_REGISTRY)) {
        tabs.push('Feeds');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_CATALOG)) {
        tabs.push('Catalog');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_INGESTION)) {
        tabs.push('Ingestion');
      }
      // Model Import is no longer a tab — it's a modal launched from Uploads
      // (gated there by MODULE_DATA_MODEL_IMPORT).
      if (canAccessFeature(FEATURES.MODULE_DATA_MDR)) {
        tabs.push('MDR');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_RECONCILIATION)) {
        tabs.push('Reconciliation');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_WORKFLOW_BUILDER)) {
        // Data Attributes (create the primitives) sits in front of Workflow
        // Builder (the canvas that composes them) — same feature gate.
        tabs.push('Data Attributes');
        tabs.push('Workflow Builder');
      }
      if (
        canAccessFeature(FEATURES.ACTION_DATA_UPLOAD)
        || canAccessFeature(FEATURES.MODULE_DATA_MODEL_IMPORT)
      ) {
        tabs.push('Uploads');
      }
      return tabs;
    },
  },
  watch: {
    etl_tabs: {
      handler(newVal) {
        if (newVal.length > 0) {
          // Fall back to the first tab if the remembered one no longer exists
          // (renamed or access revoked) — otherwise the page renders blank.
          const remembered = localStorage.getItem('etl_utility_tab');
          this.selected_tab = newVal.includes(remembered) ? remembered : newVal[0];
          this.visited.add(this.selected_tab);
        }
      },
      immediate: true,
    },
  },
  methods: {
    switch_tab(tab) {
      this.selected_tab = tab;
      this.visited = new Set([...this.visited, tab]);
      localStorage.setItem('etl_utility_tab', tab);
    },
    // A feed row asked to open its schema/routine in the Ingestion tab. The
    // focus target is already stashed in localStorage by the Feeds component;
    // switch here, then tell the (possibly already-mounted) Ingestion pane to
    // consume it. On first visit the pane's own mounted() consumes it instead —
    // applyPendingFocus is idempotent (reads+clears), so a double call is safe.
    openIngestion({ schemaId = null, routineId = null } = {}) {
      localStorage.setItem('etl_ingestion_focus', JSON.stringify({ schemaId, routineId }));
      this.switch_tab('Ingestion');
      this.$nextTick(() => {
        if (this.$refs.ingestion && this.$refs.ingestion.applyPendingFocus) {
          this.$refs.ingestion.applyPendingFocus();
        }
      });
    },
  },
};
</script>
